import { useRef, useState } from "react";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Music, Plus, Trash2, Upload } from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type MusicItem = {
  id: string;
  title: string;
  description: string | null;
  audio_url: string;
  duration_minutes: number | null;
  tags: string[];
  is_active: boolean;
  created_at: string;
};

async function fetchMusic(): Promise<MusicItem[]> {
  const res = await adminFetch("/api/admin/meditation-music");
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? "Liste alınamadı.");
  return json.items as MusicItem[];
}

async function createMusic(body: Omit<MusicItem, "id" | "created_at">): Promise<MusicItem> {
  const res = await adminFetch("/api/admin/meditation-music", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? "Kaydedilemedi.");
  return json.item as MusicItem;
}

async function deleteMusic(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/meditation-music/${encodeURIComponent(id)}`, { method: "DELETE" });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? "Silinemedi.");
}

async function uploadAudio(file: File): Promise<string> {
  const dataBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Dosya okunamadı."));
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      if (comma === -1) return reject(new Error("Dosya okunamadı."));
      resolve(result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });

  const res = await adminFetch("/api/admin/meditation-music/upload", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contentType: file.type || "audio/mpeg", dataBase64 }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? "Yükleme başarısız.");
  return json.url as string;
}

const EMPTY_FORM = {
  title: "",
  description: "",
  audio_url: "",
  duration_minutes: "",
  tags: "",
  is_active: true,
};

export default function AdminMeditasyonMuzik() {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const { sessionQuery, loginMutation } = useAdminSession();
  const authenticated = Boolean(sessionQuery.data?.authenticated);
  const configured = Boolean(sessionQuery.data?.configured);
  const queryClient = useQueryClient();

  const [form, setForm] = useState(EMPTY_FORM);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const musicQuery = useQuery({ queryKey: ["meditation-music"], queryFn: fetchMusic });
  const items = musicQuery.data ?? [];

  const createMut = useMutation({
    mutationFn: createMusic,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meditation-music"] });
      setForm(EMPTY_FORM);
      setUploadError(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteMusic,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["meditation-music"] }),
  });

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setIsUploading(true);
    try {
      const url = await uploadAudio(file);
      setForm(f => ({ ...f, audio_url: url }));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Yükleme başarısız.");
    } finally {
      setIsUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.audio_url.trim()) return;
    createMut.mutate({
      title: form.title.trim(),
      description: form.description.trim() || null,
      audio_url: form.audio_url.trim(),
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      is_active: form.is_active,
    });
  }

  if (sessionQuery.isLoading) return <div className="p-8 text-sm text-muted-foreground">Yükleniyor...</div>;

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <div className="flex items-center gap-3 border-b bg-background/80 px-4 py-3 backdrop-blur">
          <NumerolojiAdminMobileSidebar isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} mode="links" />
          <Music className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Meditasyon Müziği</h1>
        </div>

        <div className="flex-1 p-6 space-y-8 max-w-3xl">
          {/* Add form */}
          <div className="rounded-2xl border bg-background p-6 shadow-sm">
            <h2 className="mb-4 flex items-center gap-2 text-base font-semibold">
              <Plus className="h-4 w-4" /> Yeni Müzik Ekle
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="title">Başlık *</Label>
                  <Input id="title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Müzik başlığı" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="duration">Süre (dakika)</Label>
                  <Input id="duration" type="number" min={0} value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} placeholder="30" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="description">Açıklama</Label>
                <Textarea id="description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Kısa açıklama" rows={2} />
              </div>

              <div className="space-y-1.5">
                <Label>Ses Dosyası *</Label>
                <div className="flex items-center gap-3">
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={isUploading}>
                    <Upload className="mr-2 h-4 w-4" />
                    {isUploading ? "Yükleniyor..." : "Dosya Seç"}
                  </Button>
                  <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={handleFileChange} />
                  {form.audio_url && (
                    <span className="text-xs text-green-600 truncate max-w-[260px]">✓ {form.audio_url.split("/").pop()}</span>
                  )}
                </div>
                {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
                <p className="text-xs text-muted-foreground">Ya da URL gir:</p>
                <Input value={form.audio_url} onChange={e => setForm(f => ({ ...f, audio_url: e.target.value }))} placeholder="https://..." />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="tags">Etiketler</Label>
                <Input id="tags" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="meditasyon, uyku, odak (virgülle ayır)" />
              </div>

              {createMut.isError && (
                <p className="text-sm text-destructive">{(createMut.error as Error).message}</p>
              )}

              <Button type="submit" disabled={createMut.isPending || isUploading || !form.title || !form.audio_url}>
                {createMut.isPending ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </form>
          </div>

          {/* List */}
          <div className="rounded-2xl border bg-background shadow-sm">
            <div className="border-b px-6 py-4">
              <h2 className="text-base font-semibold">Müzik Listesi ({items.length})</h2>
            </div>
            {musicQuery.isLoading && <p className="p-6 text-sm text-muted-foreground">Yükleniyor...</p>}
            {items.length === 0 && !musicQuery.isLoading && (
              <p className="p-6 text-sm text-muted-foreground">Henüz müzik eklenmemiş.</p>
            )}
            <ul className="divide-y">
              {items.map(item => (
                <li key={item.id} className="flex items-center gap-4 px-6 py-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{item.title}</span>
                      {!item.is_active && <span className="text-xs text-muted-foreground">(pasif)</span>}
                    </div>
                    {item.description && <p className="text-xs text-muted-foreground truncate">{item.description}</p>}
                    <div className="flex items-center gap-3 mt-1">
                      {item.duration_minutes && (
                        <span className="text-xs text-muted-foreground">{item.duration_minutes} dk</span>
                      )}
                      {item.tags?.length > 0 && (
                        <span className="text-xs text-muted-foreground">{item.tags.join(", ")}</span>
                      )}
                      <audio src={item.audio_url} controls className="h-7 max-w-[200px]" />
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-destructive hover:text-destructive"
                    onClick={() => {
                      if (confirm(`"${item.title}" silinsin mi?`)) deleteMut.mutate(item.id);
                    }}
                    disabled={deleteMut.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
